import { stringify } from "qs";
import z from "zod";
import { post } from "./client";
import { type Nominal, type OkResponse, type TypedUrl, url } from "./common";
import type { PublicShowroomId, PublicShowroomSummary } from "./publicShowroom";
import type { SmallTeamId } from "./smallTeam";
import type { SparkId, SparkSummary, WorkshopStepSpark } from "./spark";
import type {
  InviteId,
  PendingInviteId,
  ProfileImageData,
  UserId,
  UserSummary,
} from "./user";

// ---- IDs ----

export type WorkshopId = Nominal<string, "Workshop">;
type WorkshopSettingsId = Nominal<string, "WorkshopSettings">;

// ---- Enums ----
export const schoolTypeSchema = z.enum([
  "Grundschule",
  "Gymnasium",
  "Gesamtschule / Integrierte Sekundarschule",
  "Realschule / Oberschule / Hauptschule",
  "Förderschule",
  "Gemeinschaftsschule",
  "Berufliche Schule",
  "Alternatives Schulkonzept (z. B. Montessori, Waldorf, International)",
  "Auslandsschule",
  "Andere",
  "Keine Angabe",
]);
export type SchoolType = z.infer<typeof schoolTypeSchema>;

export const bundeslandSchema = z.enum([
  "Baden-Württemberg",
  "Bayern",
  "Berlin",
  "Brandenburg",
  "Bremen",
  "Hamburg",
  "Hessen",
  "Mecklenburg-Vorpommern",
  "Niedersachsen",
  "Nordrhein-Westfalen",
  "Rheinland-Pfalz",
  "Saarland",
  "Sachsen",
  "Sachsen-Anhalt",
  "Schleswig-Holstein",
  "Thüringen",
  "Ausland",
  "Kooperation / Mehrere",
  "Keine Angabe",
]);
export type Bundesland = z.infer<typeof bundeslandSchema>;

type WorkshopState = "not completed" | "completed";

// Role values stored in the `team_roles` table.
export type TeamRoleName = "student" | "teacher";

// Roles surfaced by the vw_workshop_users view: TeamRoleName plus the
// synthetic "admin" role assigned to global admins.
export type WorkshopUserRole = TeamRoleName | "admin";

// ---- Entity ----

export type Workshop = {
  id: WorkshopId;
  name: string;
  slug: string;
  sparkId: SparkId;
  isArchived: boolean;
  createdAt: Date;
  publicShowroomId: PublicShowroomId | null;
  ownerId: UserId | null;
  schoolType: SchoolType;
  schoolLocation: Bundesland;
  schoolName: string | null;
  classSize: number | null;
  state: WorkshopState;
  completedAt: Date | null;
};

export type WorkshopSettings = {
  id: WorkshopSettingsId;
  workshopId: WorkshopId;
  libraryEnabled: boolean;
  commentsEnabled: boolean;
  projectSortMode: "creation_date" | "random";
};

// ---- Response shapes ----

// Returned by GET /workshop/:id and POST /workshop (createWorkshopForTSS).
export type WorkshopInfo = {
  id: WorkshopId;
  name: string;
  slug: string;
  sparkId: SparkId;
  isArchived: boolean;
  state: WorkshopState;
  createdAt: Date;
  owner?: UserSummary;
  roles: WorkshopUserRole[] | null;
  publicShowroom: PublicShowroomSummary | null;
  spark: SparkSummary;
};

// Returned by GET /workshop?slug= (the participant step view): folds in the
// workshop settings plus the spark's glossary flag and full schedule, all
// guaranteed present here unlike on the by-id WorkshopInfo above.
export type WorkshopStepInfo = Omit<WorkshopInfo, "spark"> & {
  spark: WorkshopStepSpark;
  settings: WorkshopSettings;
};

// Returned by GET /workshops (admin) — lighter shape than WorkshopInfo (no owner/roles/publicShowroom).
export type WorkshopListItem = {
  id: WorkshopId;
  name: string;
  slug: string;
  sparkId: SparkId;
  createdAt: Date;
  state: WorkshopState;
  isArchived: boolean;
  spark: {
    id: SparkId;
    slug: string;
    inAppTitle: string;
  };
};

// Item in GET /me/workshops — distinct shape from WorkshopInfo (no owner/sparkId field).
type MyWorkshopListItem = {
  id: WorkshopId;
  name: string;
  slug: string;
  createdAt: Date;
  isArchived: boolean;
  state: WorkshopState;
  roles: WorkshopUserRole[] | null;
  publicShowroom: PublicShowroomSummary | null;
  spark: SparkSummary;
};

export type MyWorkshopsResponse = {
  workshops: MyWorkshopListItem[];
  offset: number;
  limit: number;
  hasMore: boolean;
};

export type WorkshopParticipant = {
  id: UserId;
  role: TeamRoleName;
  name: string;
  tag: number;
  profileImageData: ProfileImageData | null;
  createdAt: Date;
  smallTeamId: SmallTeamId | null;
};

export type WorkshopUserInfoResponse = {
  participants: WorkshopParticipant[];
};

export type WorkshopPendingInvite = {
  id: PendingInviteId;
  inviteId: InviteId;
  email: string;
};

export type WorkshopStudentInvite = {
  id: InviteId;
  secret: string;
  role: "student";
  workshopId: WorkshopId;
  createdCount: number;
  createdAt: Date;
};

export type WorkshopByInviteSecretResponse = {
  ok: true;
  workshopInfo: WorkshopInfo;
};

// ---- Classroom-management live state ----

// Mirrors LiveState in the API (api/.../live/LiveState.ts). Pushed to clients
// over SSE and returned by POST /workshops/:id/manage.
// `issuedAt` (epoch ms) identifies each (re-)issue — key local UI state off it so
// a fresh broadcast resets that state, even when the content is unchanged (e.g. a
// collapsible pause dialog, or latching a message so a replay isn't re-shown).
export type PauseState = { message: string | null; issuedAt: number };
export type MessageState = { content: string; issuedAt: number };

export type LiveState = {
  pause: PauseState | null;
  message: MessageState | null;
};

// Teacher-triggered actions accepted by POST /workshops/:id/manage.
export type ManageAction =
  | { action: "startPause"; payload?: { message?: string } }
  | { action: "endPause" }
  | { action: "showMessage"; payload: { content: string } };

// ---- Models ----

export const workshopModels = {
  WORKSHOPS: (archived?: boolean) =>
    url<WorkshopListItem[]>(
      `/workshops${stringify({ archived }, { addQueryPrefix: true })}`,
    ),
  // By-id returns the base WorkshopInfo; by-slug returns the richer
  // WorkshopStepInfo (settings + spark schedule/glossary) — overloads pick the
  // precise shape per caller.
  WORKSHOP: (({ id, slug }: { id?: WorkshopId; slug?: string }) =>
    url<WorkshopInfo | WorkshopStepInfo>(
      id ? `/workshop/${id}` : `/workshop?slug=${slug}`,
    )) as {
    (opts: { id: WorkshopId; slug?: undefined }): TypedUrl<WorkshopInfo>;
    (opts: { id?: undefined; slug: string }): TypedUrl<WorkshopStepInfo>;
  },
  WORKSHOP_PENDING_INVITES: (workshopId: WorkshopId) =>
    url<WorkshopPendingInvite[]>(`/workshops/${workshopId}/invites/pending`),
  WORKSHOP_STUDENT_INVITE: (workshopId: WorkshopId) =>
    url<WorkshopStudentInvite>(`/workshops/${workshopId}/invites/student`),
  WORKSHOP_USER_INFO: (workshopId: WorkshopId) =>
    url<WorkshopUserInfoResponse>(`/workshops/${workshopId}/user-info`),
  WORKSHOP_BY_INVITE_SECRET: (secret: string) =>
    url<WorkshopByInviteSecretResponse>(`/auth/invites/${secret}/workshop`),
  WORKSHOPS_MINE: (offset = 0) =>
    url<MyWorkshopsResponse>(
      `/me/workshops${stringify({ offset }, { addQueryPrefix: true })}`,
    ),
};

// ---- Mutations ----

// Matches every paginated /me/workshops?offset=… cache entry — pages other than
// offset=0 stay cached after a user paginates, so creating or deleting a
// workshop must invalidate the whole family, not just the first page.
const matchesWorkshopsMine = (key: unknown) =>
  typeof key === "string" && key.startsWith("/me/workshops?");

type CreateWorkshopForTSSInput = {
  name: string;
  sparkId: SparkId;
  schoolType: SchoolType;
  schoolLocation: Bundesland;
  schoolName: string | null;
  classSize: number | null;
};

type UpdateWorkshopInput = {
  id: WorkshopId;
  name?: string;
  slug?: string;
  isArchived?: boolean;
  publicShowroomId?: PublicShowroomId | null;
};

type UpdateWorkshopSettingsInput = {
  workshopId: WorkshopId;
  // Needed to revalidate the by-slug workshop model that now carries settings.
  slug: string;
  libraryEnabled?: boolean;
  commentsEnabled?: boolean;
  projectSortMode?: "creation_date" | "random";
};

export const workshopMutations = {
  removeUserFromWorkshop: ({
    userId,
    workshopId,
  }: {
    workshopId: WorkshopId;
    userId: UserId;
  }) =>
    post<OkResponse>(`/workshops/${workshopId}/remove-user`, { userId }, [
      workshopModels.WORKSHOP_USER_INFO(workshopId),
    ]),

  createWorkshopForTSS: ({
    name,
    sparkId,
    schoolType,
    schoolLocation,
    schoolName,
    classSize,
  }: CreateWorkshopForTSSInput) =>
    post<WorkshopInfo>(
      "/workshop",
      { name, sparkId, schoolType, schoolLocation, schoolName, classSize },
      [matchesWorkshopsMine],
    ),

  inviteTeacher: ({ id, email }: { id: WorkshopId; email: string }) =>
    post<OkResponse>(`/workshops/${id}/inviteTeacher`, { email }, [
      workshopModels.WORKSHOP_USER_INFO(id),
      workshopModels.WORKSHOP_PENDING_INVITES(id),
    ]),

  updateWorkshop: ({
    id,
    name,
    slug,
    isArchived,
    publicShowroomId,
  }: UpdateWorkshopInput) =>
    post<Workshop>(
      `/workshops/${id}/update`,
      { name, slug, isArchived, publicShowroomId },
      [
        workshopModels.WORKSHOPS(false),
        workshopModels.WORKSHOPS(true),
        workshopModels.WORKSHOP({ id }),
      ],
    ),

  transferWorkshopOwnership: ({
    id,
    userId,
  }: {
    id: WorkshopId;
    userId: UserId;
  }) =>
    post<Workshop>(`/workshops/${id}/transfer`, { userId }, [
      workshopModels.WORKSHOP({ id }),
    ]),

  deleteWorkshop: ({ id }: { id: WorkshopId }) =>
    post<OkResponse>(`/workshops/${id}/delete`, {}, [
      matchesWorkshopsMine,
      workshopModels.WORKSHOPS(false),
      workshopModels.WORKSHOPS(true),
    ]),

  updateWorkshopSettings: ({
    workshopId,
    slug,
    libraryEnabled,
    commentsEnabled,
    projectSortMode,
  }: UpdateWorkshopSettingsInput) =>
    post<OkResponse>(
      `/workshops/${workshopId}/settings`,
      { libraryEnabled, commentsEnabled, projectSortMode },
      [workshopModels.WORKSHOP({ slug })],
    ),

  setLernreiseCompleted: ({
    workshopId,
    slug,
  }: {
    workshopId: WorkshopId;
    slug: string;
  }) =>
    post<OkResponse>(`/workshops/${workshopId}/state`, { state: "completed" }, [
      workshopModels.WORKSHOP({ id: workshopId }),
      workshopModels.WORKSHOP({ slug }),
    ]),

  // The new state is broadcast to subscribers over SSE, so nothing to
  // invalidate here — we just return it to the caller.
  manageWorkshop: ({
    workshopId,
    action,
  }: {
    workshopId: WorkshopId;
    action: ManageAction;
  }) => post<LiveState>(`/workshops/${workshopId}/manage`, action),
};
